import React, { useContext, useEffect } from 'react';
import { number, string, bool } from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  arrayOf as arrayType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType
} from '@thd-nucleus/data-sources';
import {
  useProductDeprecated,
  useQuestionsAnswersDeprecated
} from '@thd-nucleus/data-sources/react/dataModel/migration';
import { Heading } from '@thd-olt-component-react/heading';
import { SEOContent } from './SEOContent';
import { QuestionsAndAnswersMobile } from './QuestionsAndAnswersMobile';
import { QAndAUtils } from '../questions-and-answers-utils';
import './questions-and-answers.style.scss';

export const QuestionsAndAnswers = ({ itemId, startindex, pagesize, conversations }) => {
  const experienceContext = useContext(ExperienceContext);
  const { channel, hosts } = experienceContext;
  const { www,
    ugc = 'https://homedepot.ugc.bazaarvoice.com',
    bazaarVoiceApps = 'https://apps.bazaarvoice.com/deployments/homedepot/main_site/production' } = hosts || {};
  const qAndAURL = conversations
    ? `${bazaarVoiceApps}/en_US/bv.js`
    : `${ugc}/static/1999aa/bvapi.js`;
  const configureBV = () => {
    window.$BV.configure('global', {
      allowSamePageSubmission: true,
      productId: itemId,
      submissionUI: 'LIGHTBOX',
    });
    window.$BV.ui('qa', 'show_questions', {
      subjectType: 'product',
    });
  };

  /*
   * Wait for SEOContent to load and then
   * add pagination button listeners for
   * smooth scrolling
   */
  const addPaginationEventListeners = (onClick) => {
    const maxAttempts = 40;
    let iteration = 0;
    const interval = setInterval(() => {
      iteration += 1;
      if (iteration > maxAttempts) {
        clearInterval(interval);
      }
      const bvqaPageLinks = document.getElementsByClassName('BVQAPageLink');
      if (bvqaPageLinks.length > 0) {
        const unselectedLinks = Array.from(bvqaPageLinks).filter((link) => {
          return !link.className.includes('BVQASelectedPageNumber');
        });
        unselectedLinks.forEach((link) => link.getElementsByTagName('a')[0].addEventListener('click', onClick));
        clearInterval(interval);
      }
    }, 250);
  };

  const removePaginationEventListeners = (onClick) => {
    const bvqaPageLinks = document.getElementsByClassName('BVQAPageLink');
    if (bvqaPageLinks.length > 0) {
      Array.from(bvqaPageLinks).forEach((link) => {
        if (link.getElementsByTagName('a')[0]) {
          link.getElementsByTagName('a')[0].removeEventListener('click', onClick);
        }
      });
    }
  };

  /*
   * Scroll to top of Q and A section after SEOContent loads,
   * then add event listeners to updated pagination buttons
   */
  const smoothScroll = () => {
    const bvqaQuestionsId = document.getElementById('BVQAQuestionsID');
    const bvqaQuestionsIdText = bvqaQuestionsId?.innerText;
    if (channel === 'mobile') {
      QAndAUtils.scrollMobile(
        addPaginationEventListeners, smoothScroll, bvqaQuestionsIdText);
    } else {
      QAndAUtils.scrollDesktop(
        addPaginationEventListeners, smoothScroll, bvqaQuestionsIdText);
    }
  };

  const loadBV = () => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.id = 'BVQAScript';
      script.src = qAndAURL;
      document.body.appendChild(script);
    }).then(() => {
      configureBV();
      addPaginationEventListeners(smoothScroll);
    });
  };

  const loadConversatons = () => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.id = 'BVConversations';
      script.src = qAndAURL;
      document.body.appendChild(script);
    });
  };

  const checkOverlayHash = (evt) => {
    const { oldURL = '' } = evt;
    if (oldURL.indexOf('#overlay') > -1) {
      evt.preventDefault();
      const currentUrl = oldURL.substring(0, oldURL.lastIndexOf('#'));
      window.history.pushState({}, '', currentUrl);
      // below overlay driven from third party
      if (document.getElementById('BVLBWindow') && document.getElementById('BVLBOverlay')) {
        document.getElementById('BVLBWindow').style.display = 'none';
        document.getElementById('BVLBOverlay').style.display = 'none';
      }
    }
  };
  const addConversationsScript = () => {
    const content = document.createElement('div');
    content.innerHTML = `
       <div
         data-bv-show="questions"
         data-bv-product-id="${itemId}"
       >
       </div>
       `;

    document.body.appendChild(content);
  };
  useEffect(() => {
    if (conversations && document) {
      addConversationsScript();
      loadConversatons();
    } else {
      if (window) {
        window.addEventListener('hashchange', checkOverlayHash);
      }
      if (window && window.$BV) {
        addPaginationEventListeners(smoothScroll);
        configureBV();
      } else {
        loadBV();
      }
    }
    return function cleanup() {
      removePaginationEventListeners(smoothScroll);
    };
  }, [itemId]);

  // const { data, error, loading } = useDataModel('product', { variables: { itemId } });
  // const QandA = useDataModel('questionsAnswers', { variables: { itemId, startindex, pagesize } });
  const { data, error, loading } = useProductDeprecated({ itemId, domains: ['identifiers'] });
  const QandA = useQuestionsAnswersDeprecated({ itemId, startindex, pagesize });
  const qaData = (QandA || {}).data;
  const qaError = (QandA || {}).error;
  const qaLoading = (QandA || {}).loading;

  const renderNull = (qaError && !qaData) || !qaData || qaLoading;

  if (!data || (error && !data) || loading || renderNull) {
    return null;
  }
  const { product } = data || {};
  const { canonicalUrl, productLabel } = (product || {}).identifiers || {};
  const { questionsAnswers } = qaData || null;

  if (!questionsAnswers) {
    return null;
  }

  if (channel === 'mobile') {
    return (
      <QuestionsAndAnswersMobile
        wwwHost={www}
        canonicalUrl={canonicalUrl}
        itemId={itemId}
        loadBV={loadBV}
        productLabel={productLabel}
        questionsAnswers={questionsAnswers}
      />
    );
  }

  return (
    <div id="questions_answers_full" className="grid questions-and-answers">
      <div className="col-12">
        <Heading title="Questions & Answers" underline />
        <div id="BVQAContainer">
          <div className="questions-and-answers__seo-content">
            <SEOContent questionsAnswers={questionsAnswers} itemId={itemId} productLabel={productLabel} />
          </div>
        </div>
      </div>
    </div>
  );
};

QuestionsAndAnswers.displayName = 'QuestionsAndAnswers';

QuestionsAndAnswers.dataModel = {
  product: params({ itemId: stringType().isRequired() }).shape({
    canonicalUrl: stringType(),
    productLabel: stringType()
  }),
  questionsAnswers: params({
    itemId: stringType().isRequired(),
    startindex: numberType(),
    pagesize: numberType()
  }).shape({
    Includes: shapeType({
      Answers: arrayType(shapeType({
        AnswerId: stringType(),
        AnswerText: stringType()
      })),
      AnswersOrder: arrayType(stringType())
    }),
    Limit: numberType(),
    Offset: numberType(),
    Results: arrayType(shapeType({
      AnswerIds: arrayType(stringType()),
      QuestionSummary: stringType(),
      SubmissionTime: stringType(),
      UserNickname: stringType()
    })),
    TotalResults: numberType()
  })
};

QuestionsAndAnswers.propTypes = {
  itemId: string.isRequired,
  startindex: number,
  pagesize: number,
  conversations: bool
};

QuestionsAndAnswers.defaultProps = {
  startindex: 1,
  pagesize: 10,
  conversations: false
};
